import React, { useEffect, useState, useContext } from "react"
import * as yup from "yup"
import { useForm } from "react-hook-form"
import { UserContext } from "../HomeApp"

import useConfirmActionViaEmailCode from "@hooks/useConfirmActionViaEmailCode"
import useInitConfirmActionViaEmailCode from "@hooks/useInitConfirmActionViaEmailCode"

/* 
  Use this when requiring email confirmation from the user before completing an action. 
  See Backline component for implementation example.
*/

interface ConfirmActionViaEmailCodeParams {
  actionText: string
  isOpen: boolean
  setIsOpen: (boolean) => void
  setIsConfirmed: (boolean) => void
}

const RESEND_SECONDS = 60

interface ConfirmActionFormData {
  challengeCode: string
}

const schema = yup.object().shape({
  challengeCode: yup
    .string()
    .matches(/^\d{6}$/, "You need to enter the 6-digit code sent to your email")
    .required("You need to enter the numeric code sent to your email"),
})

const ConfirmActionViaEmailCode = ({
  isOpen,
  setIsOpen,
  setIsConfirmed,
  actionText,
}: ConfirmActionViaEmailCodeParams) => {
  const { user: currentUser } = useContext(UserContext)
  const [invalidCodeSubmitted, setInvalidCodeSubmitted] = useState(false)
  const [canSend, setCanSend] = useState(true)
  const [remaining, setRemaining] = useState(0)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<ConfirmActionFormData>({
    defaultValues: {
      challengeCode: "",
    },
    mode: "onChange",
  })

  const startCountdown = () => {
    setRemaining(RESEND_SECONDS)
  }

  useEffect(() => {
    if (remaining <= 0) {
      setCanSend(true)
      return
    }

    const interval = setInterval(() => {
      setRemaining((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(interval)
  }, [remaining])

  const {
    mutateAsync: initConfirmMutate,
    data: initConfirmData,
    isLoading: initConfirmIsLoading,
    isError: initConfirmIsError,
    error: initConfirmError,
  } = useInitConfirmActionViaEmailCode()
  const {
    mutate: confirmMutate,
    data: confirmData,
    isLoading: confirmIsLoading,
    isError: confirmIsError,
    error: confirmError,
  } = useConfirmActionViaEmailCode({
    onSuccess: (data) => {
      if (data) {
        setIsConfirmed(data.success)
        setInvalidCodeSubmitted(!data.success)
        setIsOpen(!data.success)
      }
    },
  })

  const resetState = () => {
    setIsConfirmed(false)
    setInvalidCodeSubmitted(false)
  }

  const sendCode = () => {
    initConfirmMutate({ actionText })
    resetState()
    setCanSend(false)
    startCountdown()
  }

  useEffect(() => {
    resetState()
    if (isOpen) {
      sendCode()
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const onSubmit = (data: ConfirmActionFormData) => {
    setInvalidCodeSubmitted(false)
    confirmMutate({
      actionText: actionText,
      challengeCode: data.challengeCode,
    })
  }

  if (!isOpen) return null
  return (
    <div>
      {(initConfirmIsError || confirmIsError) && (
        <div className="text-red">
          Error: {JSON.stringify(initConfirmError)}{" "}
          {JSON.stringify(confirmError)}
        </div>
      )}

      <div>
        {!initConfirmData || initConfirmIsLoading ? (
          <div className="h-90 py-24">
            We are sending you a confirm code to {currentUser?.email}...
          </div>
        ) : (
          <>
            <div className="pb-2">
              <div className="mt-2">
                We just sent a 6 digit confirmation code to {currentUser?.email}
                <div className="mt-1 h-6">
                  {invalidCodeSubmitted && (
                    <span className="text-sm text-red-700 italic">
                      Sorry, that code doesn&apos;t match. Please double check
                      the code and resubmit.
                    </span>
                  )}
                  {confirmData?.success && (
                    <span className="text-green-600">Validated!</span>
                  )}
                </div>
              </div>

              <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-4">
                  <input
                    {...register("challengeCode")}
                    type="text"
                    autoComplete="off"
                    placeholder="Enter the code that we just sent you"
                    className="shadow appearance-none border rounded-sm w-full text-base py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!(isValid && isDirty) || confirmIsLoading}
                  className={`w-full py-2 px-4 font-bold rounded focus:outline-none text-base ${
                    !(isValid && isDirty) || confirmIsLoading
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed opacity-50"
                      : "bg-green-700 text-white hover:bg-green-800"
                  }`}
                >
                  {confirmIsLoading ? "Loading..." : "Confirm"}
                </button>
              </form>
            </div>
            <div className="flex items-baseline py-2 text-disclosure-md">
              Didn&apos;t get the code?{" "}
              {canSend ? (
                <button
                  type="button"
                  onClick={sendCode}
                  className="text-disclosure-md text-blue-600 hover:text-blue-800 underline"
                >
                  Resend
                </button>
              ) : (
                <span className="pl-2">
                  You can resend in{" "}
                  <span className="inline-block w-4 text-center">
                    {remaining}
                  </span>{" "}
                  seconds.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ConfirmActionViaEmailCode
